<template>
  <div v-if="resolvedIds.length" class="size-panel">
    <!-- 旋转 -->
    <div class="param-row">
      <label class="param-label">Y旋转(°)</label>
      <div class="param-control">
        <input
          type="range"
          :min="0"
          :max="360"
          :step="0.01"
          :value="rotationYSlider"
          :disabled="bulk && rotYMixed"
          @input="onRotateInput($event)"
          @change="onRotateChange($event)"
          class="slider"
        />
        <input
          type="number"
          min="0"
          max="360"
          step="0.01"
          :value="rotationInputDisplay()"
          :placeholder="rotYMixed ? '—' : undefined"
          @focus="onRotateNumberFocus"
          @input="onRotateNumberInput"
          @change="onRotateNumberChange"
          @blur="onRotateNumberBlur"
          class="number-input"
        />
        <span class="param-val">{{ rotYMixed ? '—' : Number(rotationYUniform).toFixed(2) }}°</span>
      </div>
    </div>
    <div class="param-row">
      <label class="param-label">X旋转(°)</label>
      <div class="param-control">
        <input
          type="range"
          :min="-180"
          :max="180"
          :step="0.01"
          :value="rotationXSlider"
          :disabled="bulk && rotXMixed"
          @input="onRotateXInput($event)"
          @change="onRotateXChange($event)"
          class="slider"
        />
        <input
          type="number"
          min="-180"
          max="180"
          step="0.01"
          :value="rotationXDisplay"
          :placeholder="rotXMixed ? '—' : undefined"
          @input="onRotateXInputNum"
          @change="onRotateXChangeNum"
          @focus="onRotateXFocus"
          @blur="onRotateXBlur"
          class="number-input"
        />
        <span class="param-val">{{ rotXMixed ? '—' : Number(rotationXUniform).toFixed(2) }}°</span>
      </div>
    </div>
    <div class="param-row">
      <label class="param-label">Z旋转(°)</label>
      <div class="param-control">
        <input
          type="range"
          :min="-180"
          :max="180"
          :step="0.01"
          :value="rotationZSlider"
          :disabled="bulk && rotZMixed"
          @input="onRotateZInput($event)"
          @change="onRotateZChange($event)"
          class="slider"
        />
        <input
          type="number"
          min="-180"
          max="180"
          step="0.01"
          :value="rotationZDisplay"
          :placeholder="rotZMixed ? '—' : undefined"
          @input="onRotateZInputNum"
          @change="onRotateZChangeNum"
          @focus="onRotateZFocus"
          @blur="onRotateZBlur"
          class="number-input"
        />
        <span class="param-val">{{ rotZMixed ? '—' : Number(rotationZUniform).toFixed(2) }}°</span>
      </div>
    </div>
    <div class="param-row">
      <button class="reset-rot-btn" @click="resetRotation" title="将所有旋转角度恢复为 0°">复位</button>
      <button
        class="reset-rot-btn reset-params-btn"
        @click="resetParamsDefault"
        :title="'把所有几何/外观参数恢复为默认值（河宽、起伏等）'"
        :disabled="!paramDefs.length"
      >参数默认</button>
    </div>

    <!-- 多轴向缩放：等比锁定 + 尺寸紧凑区（scaleMap 上 ≥2 个独立缩放键） -->
    <div v-if="uniformSizeUi && uniformScaleDimDefs.length >= 2" class="fw-size-section">
      <div class="fw-size-head">
        <label class="fw-lock-inline">
          <input
            type="checkbox"
            :checked="uniformScaleChecked"
            @change="onUniformScaleToggle($event)"
          />
          <span>等比锁定</span>
        </label>
        <span
          class="fw-size-hint"
          :title="uniformScaleChecked ? '拖动任一项或整体比例滑块将按同一比例缩放各尺寸键' : '各尺寸键可独立调整'"
        >
          {{ uniformScaleChecked ? '尺寸联动' : '尺寸独立' }}
        </span>
      </div>
      <div v-if="uniformScaleChecked" class="fw-wdh-row fw-uniform-ratio">
        <span class="fw-wdh-lbl">整体比例</span>
        <input
          type="range"
          :min="uniRatioMin"
          :max="uniRatioMax"
          step="0.005"
          :value="uniRatioSliderValue"
          class="fw-mini-slider fw-uniform-ratio-slider"
          @input="onUniformRatioSliderInput($event)"
          @change="onUniformRatioSliderChange($event)"
        />
        <span class="fw-ratio-val">{{ uniRatioDisplay }}</span>
      </div>
      <div class="fw-wdh-row fw-wdh-numbers">
        <div v-for="p in uniformScaleDimDefs" :key="p.key" class="fw-wdh-cell">
          <span class="fw-wdh-lbl">{{ p.label }}</span>
          <input
            type="number"
            :min="p.min ?? 0"
            :max="p.max ?? 100"
            step="0.01"
            :value="uniformDimNumberDisplay(p.key)"
            class="fw-wdh-input"
            @focus="onUniformDimFocus(p.key)"
            @input="onUniformDimInput(p.key, $event)"
            @change="onUniformDimChange(p.key, $event)"
            @blur="onUniformDimBlur(p.key, $event)"
          />
        </div>
      </div>
      <div v-if="!uniformScaleChecked" class="fw-wdh-row fw-wdh-sliders">
        <div v-for="p in uniformScaleDimDefs" :key="'s-' + p.key" class="fw-wdh-slider-cell">
          <input
            type="range"
            :min="p.min ?? 0"
            :max="p.max ?? 100"
            :step="p.step ?? 0.05"
            :value="numberSliderValue(p)"
            class="fw-mini-slider"
            @input="onUniformDimSliderInput(p.key, $event)"
            @change="onUniformDimSliderChange(p.key, $event)"
          />
        </div>
      </div>
      <div v-if="extraGeometryParams.length" class="fw-extra-list">
        <template v-for="param in extraGeometryParams" :key="'box-' + param.key">
          <label v-if="param.type === 'toggle'" class="fw-extra-toggle">
            <input
              type="checkbox"
              :checked="toggleParamChecked(param)"
              @change="onToggleChange(param.key, $event)"
            />
            <span>{{ param.label }}</span>
            <span v-if="bulk && isParamMixed(param.key)" class="mixed-hint-inline">多种</span>
          </label>
          <div v-else-if="param.type === 'select'" class="fw-extra-select">
            <span class="fw-wdh-lbl">{{ param.label }}</span>
            <template v-if="param.options && param.options.length > 0">
              <select
                :value="selectParamValue(param)"
                @change="onSelectChange(param, $event)"
                class="select-input"
              >
                <option v-if="bulk && isParamMixed(param.key)" disabled value="__mixed__">—</option>
                <option v-for="opt in param.options" :key="String(opt.value)" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
            </template>
            <template v-else>
              <div v-if="isUrlLikeParam(param)" class="url-param-row">
                <input
                  type="text"
                  :value="textParamValue(param)"
                  :placeholder="bulk && isParamMixed(param.key) ? '—' : undefined"
                  @change="onTextChange(param.key, $event)"
                  class="text-input"
                />
                <button type="button" class="url-file-btn" @click="openFileForParam(param)">打开文件</button>
              </div>
              <input
                v-else
                type="text"
                :value="textParamValue(param)"
                :placeholder="bulk && isParamMixed(param.key) ? '—' : undefined"
                @change="onTextChange(param.key, $event)"
                class="text-input"
              />
            </template>
          </div>
        </template>
      </div>
    </div>

    <!-- 参数表单（根据 ComponentDef.params 动态渲染） -->
    <template v-for="param in paramDefsForList" :key="param.key">
      <div class="param-row">
        <label class="param-label">{{ param.label }}</label>
        <div class="param-control">
          <!-- 数字 -->
          <template v-if="param.type === 'number'">
            <input
              type="range"
              :min="param.min ?? 0"
              :max="param.max ?? 100"
              :step="param.step ?? 1"
              :value="numberSliderValue(param)"
              :disabled="bulk && isParamMixed(param.key)"
              @input="onParamInput(param.key, $event)"
              @change="onParamChange(param.key, $event)"
              class="slider"
            />
            <input
              type="number"
              :min="param.min ?? 0"
              :max="param.max ?? 100"
              step="0.01"
              :value="paramInputDisplay(param)"
              :placeholder="bulk && isParamMixed(param.key) ? '—' : undefined"
              @focus="onParamNumberFocus(param.key)"
              @input="onParamNumberInput(param.key, $event)"
              @change="onParamNumberChange(param.key, $event)"
              @blur="onParamNumberBlur(param.key, $event)"
              class="number-input"
            />
            <span class="param-val">{{ numberParamValText(param) }}</span>
          </template>

          <!-- 颜色 -->
          <template v-else-if="param.type === 'color'">
            <template v-if="bulk && isParamMixed(param.key)">
              <span class="mixed-hint">多种</span>
            </template>
            <input
              type="color"
              :value="colorParamValue(param)"
              @change="onColorChange(param.key, $event)"
              class="color-picker"
            />
          </template>

          <!-- 下拉 -->
          <template v-else-if="param.type === 'select'">
            <template v-if="param.options && param.options.length > 0">
              <select
                :value="selectParamValue(param)"
                @change="onSelectChange(param, $event)"
                class="select-input"
              >
                <option v-if="bulk && isParamMixed(param.key)" disabled value="__mixed__">—</option>
                <option v-for="opt in param.options" :key="String(opt.value)" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
            </template>
            <template v-else>
              <div v-if="isUrlLikeParam(param)" class="url-param-row">
                <input
                  type="text"
                  :value="textParamValue(param)"
                  :placeholder="bulk && isParamMixed(param.key) ? '—' : undefined"
                  @change="onTextChange(param.key, $event)"
                  class="text-input"
                />
                <button type="button" class="url-file-btn" @click="openFileForParam(param)">打开文件</button>
              </div>
              <input
                v-else
                type="text"
                :value="textParamValue(param)"
                :placeholder="bulk && isParamMixed(param.key) ? '—' : undefined"
                @change="onTextChange(param.key, $event)"
                class="text-input"
              />
            </template>
          </template>

          <!-- 开关 -->
          <template v-else-if="param.type === 'toggle'">
            <input
              type="checkbox"
              :checked="toggleParamChecked(param)"
              @change="onToggleChange(param.key, $event)"
            />
            <span v-if="bulk && isParamMixed(param.key)" class="mixed-hint-inline">多种</span>
          </template>
        </div>
      </div>
    </template>
    <div v-if="!uniformSizeUi && extraGeometryParams.length" class="fw-extra-list fw-extra-list--plain">
      <template v-for="param in extraGeometryParams" :key="'plain-' + param.key">
        <label v-if="param.type === 'toggle'" class="fw-extra-toggle">
          <input
            type="checkbox"
            :checked="toggleParamChecked(param)"
            @change="onToggleChange(param.key, $event)"
          />
          <span>{{ param.label }}</span>
          <span v-if="bulk && isParamMixed(param.key)" class="mixed-hint-inline">多种</span>
        </label>
        <div v-else-if="param.type === 'select'" class="fw-extra-select">
          <span class="fw-wdh-lbl">{{ param.label }}</span>
          <template v-if="param.options && param.options.length > 0">
            <select
              :value="selectParamValue(param)"
              @change="onSelectChange(param, $event)"
              class="select-input"
            >
              <option v-if="bulk && isParamMixed(param.key)" disabled value="__mixed__">—</option>
              <option v-for="opt in param.options" :key="String(opt.value)" :value="opt.value">
                {{ opt.label }}
              </option>
            </select>
          </template>
          <template v-else>
            <div v-if="isUrlLikeParam(param)" class="url-param-row">
              <input
                type="text"
                :value="textParamValue(param)"
                :placeholder="bulk && isParamMixed(param.key) ? '—' : undefined"
                @change="onTextChange(param.key, $event)"
                class="text-input"
              />
              <button type="button" class="url-file-btn" @click="openFileForParam(param)">打开文件</button>
            </div>
            <input
              v-else
              type="text"
              :value="textParamValue(param)"
              :placeholder="bulk && isParamMixed(param.key) ? '—' : undefined"
              @change="onTextChange(param.key, $event)"
              class="text-input"
            />
          </template>
        </div>
      </template>
    </div>
    <input
      ref="urlFileInputRef"
      type="file"
      accept="image/*"
      class="url-hidden-file"
      @change="onUrlFileChosen($event)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, nextTick, watch } from 'vue'
import { useSceneStore } from '../stores/scene'
import { registry } from '../components-lib/registry'
import { fullRebuild } from '../three/ObjectManager'
import type { ParamDef, SceneObject } from '../types/scene'
import { getParamSection, isParamVisible } from '../utils/paramSection'
import {
  getScaleDimensionKeys,
  spreadUniformFromDriver,
  snapUniformFromCurrent,
  naturalValueForKey,
  getCurrentUniformRatio,
  getUniformRatioRange,
} from '../utils/foodWaterUniform'

const props = defineProps<{
  /** 兼容单选：仅传 id */
  id?: string
  /** 批量：多个同类型对象 id（可与 id 二选一，优先 objectIds） */
  objectIds?: string[]
  /** 为 true 时只渲染 geometry section；开关/下拉左对齐放在尺寸区内 */
  geometryOnly?: boolean
  onHistoryPush?: () => void
}>()

const sceneStore = useSceneStore()
const pushHistory = () => props.onHistoryPush?.()

function isImportedLike(type: string) {
  return type === 'imported_model' || type === 'dxf_reference'
}

const resolvedIds = computed(() => {
  if (props.objectIds?.length) return [...props.objectIds]
  if (props.id) return [props.id]
  return []
})

const bulk = computed(() => resolvedIds.value.length > 1)

const objs = computed(() => {
  const list: SceneObject[] = []
  const all = sceneStore.activeObjects()
  for (const id of resolvedIds.value) {
    const o = all.find(x => x.id === id)
    if (o) list.push(o)
  }
  return list
})

const paramDefs = computed<ParamDef[]>(() => {
  const o = objs.value[0]
  if (!o) return []
  const all = (registry.get(o.type)?.params ?? []).filter(p => isParamVisible(p, o.params))
  if (props.geometryOnly) return all.filter(p => getParamSection(p) === 'geometry')
  return all
})

/** 单选 + 变换 Tab 几何区：scaleMap 上 ≥2 个缩放键时用紧凑块展示尺寸 */
const sizeComponentDef = computed(() => {
  if (bulk.value || !props.geometryOnly) return undefined
  const o = objs.value[0]
  if (!o) return undefined
  return registry.get(o.type)
})

const uniformScaleDimKeys = computed(() => {
  const def = sizeComponentDef.value
  if (!def) return [] as string[]
  return getScaleDimensionKeys(def)
})

const uniformSizeUi = computed(
  () => !bulk.value && props.geometryOnly && uniformScaleDimKeys.value.length >= 2,
)

const uniformScaleDimDefs = computed((): ParamDef[] => {
  const keys = uniformScaleDimKeys.value
  const out: ParamDef[] = []
  for (const k of keys) {
    const p = paramDefs.value.find((x) => x.key === k && x.type === 'number')
    if (p) out.push(p)
  }
  return out
})

const uniformScaleChecked = computed(() => {
  const o = objs.value[0]
  return Boolean(o?.params?.uniformScale)
})

const uniRatioMin = computed(() => {
  const def = sizeComponentDef.value
  if (!def) return 1
  return getUniformRatioRange(def).rMin
})

const uniRatioMax = computed(() => {
  const def = sizeComponentDef.value
  if (!def) return 1
  return getUniformRatioRange(def).rMax
})

const uniRatioSliderValue = computed(() => {
  const def = sizeComponentDef.value
  const o = objs.value[0]
  if (!def || !o) return 1
  return getCurrentUniformRatio(def, o.params)
})

const uniRatioDisplay = computed(() => uniRatioSliderValue.value.toFixed(3))

const extraGeometryParams = computed(() =>
  paramDefs.value.filter((p) => p.type === 'toggle' || p.type === 'select')
)

const paramDefsForList = computed(() => {
  const hide = new Set(extraGeometryParams.value.map((p) => p.key))
  if (uniformSizeUi.value) {
    for (const k of uniformScaleDimKeys.value) hide.add(k)
  }
  return paramDefs.value.filter((p) => !hide.has(p.key))
})

const editingUniformDims = ref<Record<string, string>>({})

function samePrimitive(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (typeof a === 'number' && typeof b === 'number' && Number.isNaN(a) && Number.isNaN(b)) return true
  return false
}

function sameParamVal(a: unknown, b: unknown): boolean {
  if (samePrimitive(a, b)) return true
  if (typeof a === 'object' || typeof b === 'object') {
    try {
      return JSON.stringify(a) === JSON.stringify(b)
    } catch {
      return false
    }
  }
  return false
}

function aggNumber(getter: (o: SceneObject) => number): { uniform: number; mixed: boolean } {
  const os = objs.value
  if (os.length === 0) return { uniform: 0, mixed: false }
  const first = getter(os[0]!)
  const mixed = os.some(o => getter(o) !== first)
  return { uniform: first, mixed }
}

const rotY = computed(() => aggNumber(o => o.rotationY ?? 0))
const rotXM = computed(() => aggNumber(o => o.rotationX ?? 0))
const rotZM = computed(() => aggNumber(o => o.rotationZ ?? 0))

const rotYMixed = computed(() => bulk.value && rotY.value.mixed)
const rotXMixed = computed(() => bulk.value && rotXM.value.mixed)
const rotZMixed = computed(() => bulk.value && rotZM.value.mixed)

const rotationYUniform = computed(() => rotY.value.uniform)
const rotationXUniform = computed(() => rotXM.value.uniform)
const rotationZUniform = computed(() => rotZM.value.uniform)

const rotationYSlider = computed(() => (rotYMixed.value ? 0 : rotationYUniform.value))
const rotationXSlider = computed(() => (rotXMixed.value ? 0 : rotationXUniform.value))
const rotationZSlider = computed(() => (rotZMixed.value ? 0 : rotationZUniform.value))

const editingRotation = ref<string | null>(null)
const editingRotationX = ref<string | null>(null)
const editingRotationZ = ref<string | null>(null)
const editingParam = ref<Record<string, string>>({})

function isParamMixed(key: string): boolean {
  if (!bulk.value) return false
  const os = objs.value
  if (os.length < 2) return false
  const def = paramDefs.value.find(p => p.key === key)
  const first = os[0]!.params?.[key] ?? def?.default
  return os.some(o => !sameParamVal(o.params?.[key] ?? def?.default, first))
}

function paramAggNumber(key: string, def: ParamDef): { uniform: number; mixed: boolean } {
  const os = objs.value
  if (os.length === 0) return { uniform: def.default ?? 0, mixed: false }
  const firstRaw = os[0]!.params?.[key] ?? def.default
  const first = Number(firstRaw)
  const mixed = os.some(o => Number(o.params?.[key] ?? def.default) !== first)
  return { uniform: Number.isNaN(first) ? Number(def.default) : first, mixed: bulk.value && mixed }
}

function numberSliderValue(param: ParamDef): number {
  const { uniform, mixed } = paramAggNumber(param.key, param)
  if (bulk.value && mixed) return Number(((param.min ?? 0) + (param.max ?? 100)) / 2)
  return uniform
}

function numberParamValText(param: ParamDef): string {
  const { uniform, mixed } = paramAggNumber(param.key, param)
  if (bulk.value && mixed) return '—'
  return uniform.toFixed(2)
}

function colorParamValue(param: ParamDef): string {
  const os = objs.value[0]
  const v = os?.params?.[param.key] ?? param.default
  return typeof v === 'string' ? v : '#888888'
}

function selectParamValue(param: ParamDef): string {
  if (bulk.value && isParamMixed(param.key)) return '__mixed__'
  const os = objs.value[0]
  const v = os?.params?.[param.key] ?? param.default
  return String(v)
}

function textParamValue(param: ParamDef): string {
  if (bulk.value && isParamMixed(param.key)) return ''
  const os = objs.value[0]
  const v = os?.params?.[param.key] ?? param.default
  return String(v ?? '')
}

function toggleParamChecked(param: ParamDef): boolean {
  if (bulk.value && isParamMixed(param.key)) return false
  const os = objs.value[0]
  return Boolean(os?.params?.[param.key] ?? param.default)
}

function rotationInputDisplay() {
  if (rotYMixed.value) return editingRotation.value ?? ''
  return editingRotation.value !== null ? editingRotation.value : Number(rotationYUniform.value).toFixed(2)
}

const rotationXDisplay = computed(() => {
  if (rotXMixed.value && editingRotationX.value === null) return ''
  return editingRotationX.value !== null ? editingRotationX.value : Number(rotationXUniform.value).toFixed(2)
})

const rotationZDisplay = computed(() => {
  if (rotZMixed.value && editingRotationZ.value === null) return ''
  return editingRotationZ.value !== null ? editingRotationZ.value : Number(rotationZUniform.value).toFixed(2)
})

function paramInputDisplay(param: ParamDef) {
  const key = param.key
  if (bulk.value && isParamMixed(key)) {
    return editingParam.value[key] ?? ''
  }
  if (editingParam.value[key] !== undefined) return editingParam.value[key]!
  const { uniform } = paramAggNumber(key, param)
  return Number(uniform).toFixed(2)
}

function rebuildIds(ids: string[]) {
  const all = sceneStore.activeObjects()
  for (const id of ids) {
    const u = all.find(o => o.id === id)
    if (!u) continue
    // 导入模型 / DXF：变换与缩放由 syncObjects 增量同步，禁止整对象重建（避免反复从 IndexedDB 加载）
    if (isImportedLike(u.type)) continue
    fullRebuild(u)
  }
}

function onRotateInput(e: Event) {
  const val = Number((e.target as HTMLInputElement).value)
  if (bulk.value) {
    for (const id of resolvedIds.value) {
      sceneStore.updateObject(id, { rotationY: val })
    }
    rebuildIds(resolvedIds.value)
    return
  }
  pushHistory()
  sceneStore.updateObject(resolvedIds.value[0]!, { rotationY: val })
  rebuildIds(resolvedIds.value)
}

function onRotateChange(e: Event) {
  if (!bulk.value) return
  const val = Number((e.target as HTMLInputElement).value)
  pushHistory()
  for (const id of resolvedIds.value) {
    sceneStore.updateObject(id, { rotationY: val })
  }
  rebuildIds(resolvedIds.value)
}

function onRotateNumberFocus() {
  if (rotYMixed.value) editingRotation.value = ''
  else editingRotation.value = Number(rotationYUniform.value).toFixed(2)
}

function onRotateNumberInput(e: Event) {
  editingRotation.value = (e.target as HTMLInputElement).value
}

function commitRotation(valueStr: string) {
  const val = Number(valueStr)
  if (Number.isNaN(val)) {
    editingRotation.value = null
    return
  }
  const clamped = Math.min(360, Math.max(0, val))
  pushHistory()
  for (const id of resolvedIds.value) {
    sceneStore.updateObject(id, { rotationY: clamped })
  }
  rebuildIds(resolvedIds.value)
  editingRotation.value = null
}

function onRotateNumberChange(e: Event) {
  commitRotation((e.target as HTMLInputElement).value)
}

function onRotateNumberBlur(e: Event) {
  commitRotation((e.target as HTMLInputElement).value)
}

function onRotateXInput(e: Event) {
  const val = Number((e.target as HTMLInputElement).value)
  if (bulk.value) {
    for (const id of resolvedIds.value) {
      sceneStore.updateObject(id, { rotationX: val })
    }
    rebuildIds(resolvedIds.value)
    return
  }
  pushHistory()
  sceneStore.updateObject(resolvedIds.value[0]!, { rotationX: val })
  rebuildIds(resolvedIds.value)
}

function onRotateXChange(e: Event) {
  if (!bulk.value) return
  const val = Number((e.target as HTMLInputElement).value)
  pushHistory()
  for (const id of resolvedIds.value) {
    sceneStore.updateObject(id, { rotationX: val })
  }
  rebuildIds(resolvedIds.value)
}

function onRotateXFocus() {
  if (rotXMixed.value) editingRotationX.value = ''
  else editingRotationX.value = Number(rotationXUniform.value).toFixed(2)
}

function onRotateXInputNum(e: Event) {
  editingRotationX.value = (e.target as HTMLInputElement).value
}

function onRotateXBlur(e: Event) {
  onRotateXChangeNum(e)
}

function onRotateXChangeNum(e: Event) {
  const val = Math.min(180, Math.max(-180, Number((e.target as HTMLInputElement).value)))
  if (Number.isNaN(val)) {
    editingRotationX.value = null
    return
  }
  pushHistory()
  for (const id of resolvedIds.value) {
    sceneStore.updateObject(id, { rotationX: val })
  }
  rebuildIds(resolvedIds.value)
  editingRotationX.value = null
}

function onRotateZInput(e: Event) {
  const val = Number((e.target as HTMLInputElement).value)
  if (bulk.value) {
    for (const id of resolvedIds.value) {
      sceneStore.updateObject(id, { rotationZ: val })
    }
    rebuildIds(resolvedIds.value)
    return
  }
  pushHistory()
  sceneStore.updateObject(resolvedIds.value[0]!, { rotationZ: val })
  rebuildIds(resolvedIds.value)
}

function onRotateZChange(e: Event) {
  if (!bulk.value) return
  const val = Number((e.target as HTMLInputElement).value)
  pushHistory()
  for (const id of resolvedIds.value) {
    sceneStore.updateObject(id, { rotationZ: val })
  }
  rebuildIds(resolvedIds.value)
}

function onRotateZFocus() {
  if (rotZMixed.value) editingRotationZ.value = ''
  else editingRotationZ.value = Number(rotationZUniform.value).toFixed(2)
}

function onRotateZInputNum(e: Event) {
  editingRotationZ.value = (e.target as HTMLInputElement).value
}

function onRotateZBlur(e: Event) {
  onRotateZChangeNum(e)
}

function onRotateZChangeNum(e: Event) {
  const val = Math.min(180, Math.max(-180, Number((e.target as HTMLInputElement).value)))
  if (Number.isNaN(val)) {
    editingRotationZ.value = null
    return
  }
  pushHistory()
  for (const id of resolvedIds.value) {
    sceneStore.updateObject(id, { rotationZ: val })
  }
  rebuildIds(resolvedIds.value)
  editingRotationZ.value = null
}

function resetRotation() {
  editingRotation.value = null
  editingRotationX.value = null
  editingRotationZ.value = null
  pushHistory()
  for (const id of resolvedIds.value) {
    sceneStore.updateObject(id, { rotationY: 0, rotationX: 0, rotationZ: 0 })
  }
  rebuildIds(resolvedIds.value)
}

/** 把当前对象的组件参数恢复为 ComponentDef 里声明的 default（河宽、起伏等），避免调乱后不知如何恢复 */
function resetParamsDefault() {
  const defs = paramDefs.value
  if (!defs.length) return
  editngParamClear()
  pushHistory()
  for (const id of resolvedIds.value) {
    const patch: Record<string, unknown> = {}
    for (const p of defs) {
      patch[p.key] = p.default
    }
    sceneStore.updateObjectParams(id, patch)
  }
  rebuildIds(resolvedIds.value)
}

function editngParamClear() {
  editingParam.value = {}
  editingUniformDims.value = {}
}

function onParamInput(key: string, e: Event) {
  const val = Number((e.target as HTMLInputElement).value)
  const param = paramDefs.value.find(p => p.key === key)
  if (!param) return
  if (bulk.value) {
    for (const id of resolvedIds.value) {
      const current = sceneStore.activeObjects().find(o => o.id === id)
      if (!current) continue
      if (isImportedLike(current.type) && key === 'userScale') {
        sceneStore.updateObjectParams(id, { [key]: val })
        continue
      }
      const tempObj = { ...current, params: { ...current.params, [key]: val } }
      fullRebuild(tempObj)
    }
    return
  }
  const current = sceneStore.activeObjects().find(o => o.id === resolvedIds.value[0])
  if (!current) return
  if (isImportedLike(current.type) && key === 'userScale') {
    sceneStore.updateObjectParams(resolvedIds.value[0]!, { [key]: val })
    return
  }
  const tempObj = { ...current, params: { ...current.params, [key]: val } }
  fullRebuild(tempObj)
}

function onParamChange(key: string, e: Event) {
  const val = Number((e.target as HTMLInputElement).value)
  if (bulk.value) {
    pushHistory()
    for (const id of resolvedIds.value) {
      sceneStore.updateObjectParams(id, { [key]: val })
    }
    rebuildIds(resolvedIds.value)
    return
  }
  pushHistory()
  sceneStore.updateObjectParams(resolvedIds.value[0]!, { [key]: val })
  rebuildIds(resolvedIds.value)
}

function onParamNumberFocus(key: string) {
  const def = paramDefs.value.find(p => p.key === key)
  if (bulk.value && isParamMixed(key)) {
    editingParam.value = { ...editingParam.value, [key]: '' }
    return
  }
  const current = objs.value[0]?.params?.[key] ?? def?.default ?? 0
  editingParam.value = { ...editingParam.value, [key]: String(current) }
}

function onParamNumberInput(key: string, e: Event) {
  editingParam.value = { ...editingParam.value, [key]: (e.target as HTMLInputElement).value }
  const val = Number((e.target as HTMLInputElement).value)
  if (Number.isNaN(val)) return
  const param = paramDefs.value.find(p => p.key === key)
  if (!param) return
  if (bulk.value) {
    for (const id of resolvedIds.value) {
      const current = sceneStore.activeObjects().find(o => o.id === id)
      if (!current) continue
      if (isImportedLike(current.type) && key === 'userScale') {
        sceneStore.updateObjectParams(id, { [key]: val })
        continue
      }
      const tempObj = { ...current, params: { ...current.params, [key]: val } }
      fullRebuild(tempObj)
    }
    return
  }
  const current = sceneStore.activeObjects().find(o => o.id === resolvedIds.value[0])
  if (!current) return
  if (isImportedLike(current.type) && key === 'userScale') {
    sceneStore.updateObjectParams(resolvedIds.value[0]!, { [key]: val })
    return
  }
  const tempObj = { ...current, params: { ...current.params, [key]: val } }
  fullRebuild(tempObj)
}

function commitParamNumber(key: string, valueStr: string) {
  const val = Number(valueStr)
  const paramDef = paramDefs.value.find(p => p.key === key)
  const min = paramDef?.min ?? 0
  const max = paramDef?.max ?? 100
  const next = { ...editingParam.value }
  delete next[key]
  editingParam.value = next
  if (Number.isNaN(val)) return
  const clamped = Math.min(max, Math.max(min, val))
  pushHistory()
  for (const id of resolvedIds.value) {
    sceneStore.updateObjectParams(id, { [key]: clamped })
  }
  rebuildIds(resolvedIds.value)
}

function onParamNumberChange(key: string, e: Event) {
  commitParamNumber(key, (e.target as HTMLInputElement).value)
}

function onParamNumberBlur(key: string, e: Event) {
  commitParamNumber(key, (e.target as HTMLInputElement).value)
}

function onColorChange(key: string, e: Event) {
  const val = (e.target as HTMLInputElement).value
  pushHistory()
  for (const id of resolvedIds.value) {
    sceneStore.updateObjectParams(id, { [key]: val })
  }
  rebuildIds(resolvedIds.value)
}

function onSelectChange(param: ParamDef, e: Event) {
  const raw = (e.target as HTMLSelectElement).value
  if (raw === '__mixed__') return
  const matched = param.options?.find((opt) => String(opt.value) === raw)
  const val = matched ? matched.value : raw
  pushHistory()
  for (const id of resolvedIds.value) {
    sceneStore.updateObjectParams(id, { [param.key]: val })
  }
  rebuildIds(resolvedIds.value)
}

function onTextChange(key: string, e: Event) {
  const val = (e.target as HTMLInputElement).value
  pushHistory()
  for (const id of resolvedIds.value) {
    sceneStore.updateObjectParams(id, { [key]: val })
  }
  rebuildIds(resolvedIds.value)
}

/** URL/图片类自由文本参数：附加「打开文件」按钮，避免手填超长文本 */
function isUrlLikeParam(p: ParamDef): boolean {
  if (p.type !== 'select' || (p.options && p.options.length > 0)) return false
  const key = p.key.toLowerCase()
  const label = p.label
  return key.includes('url') || key.includes('image') || label.includes('URL') || label.includes('图片')
}

const urlFileInputRef = ref<HTMLInputElement | null>(null)
let urlParamKey = ''

function openFileForParam(p: ParamDef) {
  urlParamKey = p.key
  urlFileInputRef.value?.click()
}

function onUrlFileChosen(e: Event) {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file || !urlParamKey) return
  const reader = new FileReader()
  reader.onload = () => {
    const url = String(reader.result ?? '')
    const key = urlParamKey
    urlParamKey = ''
    pushHistory()
    for (const id of resolvedIds.value) {
      sceneStore.updateObjectParams(id, { [key]: url })
    }
    rebuildIds(resolvedIds.value)
  }
  reader.onerror = () => { urlParamKey = '' }
  reader.readAsDataURL(file)
}

function onToggleChange(key: string, e: Event) {
  const val = (e.target as HTMLInputElement).checked
  pushHistory()
  for (const id of resolvedIds.value) {
    sceneStore.updateObjectParams(id, { [key]: val })
  }
  nextTick(() => rebuildIds(resolvedIds.value))
}

function uniformDimNumberDisplay(key: string): string {
  if (editingUniformDims.value[key] !== undefined) return editingUniformDims.value[key]!
  const p = paramDefs.value.find((x) => x.key === key)
  if (!p) return ''
  const { uniform } = paramAggNumber(key, p)
  return Number(uniform).toFixed(2)
}

function onUniformDimFocus(key: string) {
  const p = paramDefs.value.find((x) => x.key === key)
  if (!p) return
  const { uniform } = paramAggNumber(key, p)
  editingUniformDims.value = { ...editingUniformDims.value, [key]: String(uniform) }
}

function isUniformScaleDimKey(key: string): boolean {
  return uniformScaleDimKeys.value.includes(key)
}

function applyUniformDimLive(key: string, val: number) {
  if (!isUniformScaleDimKey(key)) return
  const id = resolvedIds.value[0]!
  const current = sceneStore.activeObjects().find((o) => o.id === id)
  const def = sizeComponentDef.value
  if (!current || !def || Number.isNaN(val)) return
  let nextParams: Record<string, any> = { ...current.params, [key]: val }
  if (current.params?.uniformScale) {
    nextParams = { ...nextParams, ...spreadUniformFromDriver(def, key, val) }
  }
  const tempObj = { ...current, params: nextParams }
  fullRebuild(tempObj)
}

function onUniformDimInput(key: string, e: Event) {
  const raw = (e.target as HTMLInputElement).value
  editingUniformDims.value = { ...editingUniformDims.value, [key]: raw }
  const val = Number(raw)
  if (Number.isNaN(val)) return
  applyUniformDimLive(key, val)
}

function commitUniformDim(key: string, valueStr: string) {
  if (!isUniformScaleDimKey(key)) return
  const val = Number(valueStr)
  const next = { ...editingUniformDims.value }
  delete next[key]
  editingUniformDims.value = next
  if (Number.isNaN(val)) return
  const p = paramDefs.value.find((x) => x.key === key)
  if (!p) return
  const min = p.min ?? 0
  const max = p.max ?? 100
  const clamped = Math.min(max, Math.max(min, val))
  pushHistory()
  const id = resolvedIds.value[0]!
  const current = sceneStore.activeObjects().find((o) => o.id === id)
  const def = sizeComponentDef.value
  if (!current || !def) return
  let patch: Record<string, any> = { [key]: clamped }
  if (current.params?.uniformScale) {
    patch = { ...spreadUniformFromDriver(def, key, clamped), uniformScale: true }
  }
  sceneStore.updateObjectParams(id, patch)
  rebuildIds([id])
}

function onUniformDimChange(key: string, e: Event) {
  commitUniformDim(key, (e.target as HTMLInputElement).value)
}

function onUniformDimBlur(key: string, e: Event) {
  commitUniformDim(key, (e.target as HTMLInputElement).value)
}

function onUniformDimSliderInput(key: string, e: Event) {
  const val = Number((e.target as HTMLInputElement).value)
  if (Number.isNaN(val)) return
  applyUniformDimLive(key, val)
}

function onUniformDimSliderChange(key: string, e: Event) {
  const val = Number((e.target as HTMLInputElement).value)
  if (Number.isNaN(val)) return
  if (!isUniformScaleDimKey(key)) return
  pushHistory()
  const id = resolvedIds.value[0]!
  const current = sceneStore.activeObjects().find((o) => o.id === id)
  const def = sizeComponentDef.value
  if (!current || !def) return
  let patch: Record<string, any> = { [key]: val }
  if (current.params?.uniformScale) {
    patch = { ...spreadUniformFromDriver(def, key, val), uniformScale: true }
  }
  sceneStore.updateObjectParams(id, patch)
  rebuildIds([id])
}

function onUniformRatioSliderInput(e: Event) {
  const r = Number((e.target as HTMLInputElement).value)
  const def = sizeComponentDef.value
  const id = resolvedIds.value[0]
  if (!def || !id || Number.isNaN(r)) return
  const keys = getScaleDimensionKeys(def)
  const driver = keys[0]!
  const nat = naturalValueForKey(def, driver)
  const current = sceneStore.activeObjects().find((o) => o.id === id)
  if (!current) return
  const patch = spreadUniformFromDriver(def, driver, nat * r)
  const tempObj = { ...current, params: { ...current.params, ...patch, uniformScale: true } }
  fullRebuild(tempObj)
}

function onUniformRatioSliderChange(e: Event) {
  const r = Number((e.target as HTMLInputElement).value)
  const def = sizeComponentDef.value
  const id = resolvedIds.value[0]
  if (!def || !id || Number.isNaN(r)) return
  pushHistory()
  const keys = getScaleDimensionKeys(def)
  const driver = keys[0]!
  const nat = naturalValueForKey(def, driver)
  const patch = { ...spreadUniformFromDriver(def, driver, nat * r), uniformScale: true }
  sceneStore.updateObjectParams(id, patch)
  rebuildIds([id])
}

function onUniformScaleToggle(e: Event) {
  const checked = (e.target as HTMLInputElement).checked
  pushHistory()
  const id = resolvedIds.value[0]!
  const current = sceneStore.activeObjects().find((o) => o.id === id)
  const def = sizeComponentDef.value
  if (!current || !def) return
  if (checked) {
    const snap = snapUniformFromCurrent(def, current.params)
    sceneStore.updateObjectParams(id, { uniformScale: true, ...snap })
  } else {
    sceneStore.updateObjectParams(id, { uniformScale: false })
  }
  nextTick(() => rebuildIds([id]))
}

watch(
  () => [...resolvedIds.value],
  () => {
    editingRotation.value = null
    editingRotationX.value = null
    editingRotationZ.value = null
    editingParam.value = {}
    editingUniformDims.value = {}
  }
)
</script>

<style scoped>
.size-panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.param-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
}

.param-label {
  font-size: 11px;
  color: #88aacc;
  min-width: 64px;
  flex-shrink: 0;
  text-align: left;
}

.param-control {
  flex: 0 1 auto;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  min-width: 0;
}

.slider {
  flex: 0 0 96px;
  width: 96px;
  height: 4px;
  accent-color: #4a9eff;
  cursor: pointer;
}

.slider:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.param-val {
  font-size: 11px;
  color: #aaccee;
  min-width: 44px;
  text-align: left;
}

.number-input + .param-val {
  display: none;
}

.mixed-hint {
  font-size: 10px;
  color: #6a7a90;
  margin-right: 4px;
}

.mixed-hint-inline {
  font-size: 10px;
  color: #6a7a90;
  margin-left: 4px;
}

.number-input {
  width: 56px;
  flex-shrink: 0;
  background: #243040;
  border: 1px solid #3a4a60;
  color: #cce;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 11px;
  outline: none;
}

.number-input:focus {
  border-color: #4a9eff;
}

.color-picker {
  width: 36px;
  height: 24px;
  border: 1px solid #3a4a60;
  border-radius: 3px;
  padding: 0;
  cursor: pointer;
  background: none;
}

.select-input,
.text-input {
  flex: 1;
  background: #243040;
  border: 1px solid #3a4a60;
  color: #cce;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 11px;
  outline: none;
}

.select-input:focus,
.text-input:focus {
  border-color: #4a9eff;
}

.reset-rot-btn {
  padding: 4px 12px;
  background: #243040;
  border: 1px solid #3a4a60;
  color: #8899aa;
  border-radius: 4px;
  font-size: 11px;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
}
.reset-rot-btn:hover {
  background: #2e4060;
  border-color: #4a6a8a;
  color: #aabbcc;
}
.reset-params-btn {
  border-color: rgba(74, 158, 255, 0.45);
  color: #9ab8d8;
}
.reset-params-btn:hover:not(:disabled) {
  border-color: #4a9eff;
  color: #cfe4ff;
}
.reset-params-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* 食品矿泉水尺寸：横向紧凑，少占纵向空间 */
.fw-size-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 6px 8px;
  margin: 2px 0 6px;
  background: rgba(30, 45, 65, 0.45);
  border: 1px solid rgba(74, 110, 160, 0.35);
  border-radius: 6px;
}

.fw-size-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}

.fw-lock-inline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #aaccee;
  cursor: pointer;
  user-select: none;
}

.fw-size-hint {
  font-size: 10px;
  color: #6a8aaa;
  flex: 1;
  text-align: right;
  min-width: 0;
}

.fw-wdh-row {
  display: flex;
  gap: 6px;
  align-items: flex-end;
}

.fw-wdh-numbers .fw-wdh-cell {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.fw-wdh-lbl {
  font-size: 10px;
  color: #88aacc;
  white-space: nowrap;
}

.fw-wdh-input {
  width: 100%;
  box-sizing: border-box;
  background: #1a2838;
  border: 1px solid #3a4a60;
  color: #ddeeff;
  border-radius: 4px;
  padding: 4px 4px;
  font-size: 11px;
  outline: none;
}

.fw-wdh-input:focus {
  border-color: #4a9eff;
}

.fw-wdh-sliders {
  margin-top: 2px;
}

.fw-wdh-slider-cell {
  flex: 1;
  min-width: 0;
}

.fw-mini-slider {
  width: 100%;
  height: 3px;
  accent-color: #4a9eff;
  cursor: pointer;
}

.fw-uniform-ratio {
  align-items: center;
  gap: 8px;
}

.fw-uniform-ratio-slider {
  flex: 1;
  min-width: 0;
}

.fw-ratio-val {
  font-size: 11px;
  color: #aaccee;
  min-width: 48px;
  text-align: right;
  flex-shrink: 0;
}

.fw-extra-list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding-top: 6px;
  border-top: 1px solid rgba(74, 110, 160, 0.25);
}

.fw-extra-list--plain {
  padding-top: 0;
  border-top: none;
}

.fw-extra-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  font-size: 11px;
  color: #aaccee;
  cursor: pointer;
  user-select: none;
}

.fw-extra-select {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  width: 100%;
}

.fw-extra-select .fw-wdh-lbl {
  flex-shrink: 0;
  font-size: 11px;
}

.fw-extra-select .select-input {
  flex: 1;
  min-width: 0;
}

/* URL/图片类自由文本参数：文本 + 打开文件按钮 */
.url-param-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.url-param-row .text-input {
  flex: 1;
  min-width: 0;
}

.url-file-btn {
  flex-shrink: 0;
  padding: 3px 8px;
  background: #243040;
  border: 1px solid #3a4a60;
  border-radius: 4px;
  color: #aac;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.15s;
}
.url-file-btn:hover {
  background: #2e4060;
  border-color: #4a9eff;
  color: #ddf;
}

.url-hidden-file {
  display: none;
}
</style>
